import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, TextField,
  Tooltip, Typography,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Form } from '@formio/react';
import axios from 'axios';
import moment from 'moment';
import { APIURL } from '../../config';
import { StatusCard } from './OutboundReferralsControlRoom';
import ReferralAnswers, { referralAnswers, filledAnswer } from './ReferralAnswers';
import { referralDateOfBirth, referralProspectAddress } from './referralProspectDetails';

const STATUS_META = {
  RECEIVED: { label: 'Awaiting Decision', color: '#3fa7d6' },
};
const STATUS_COLORS = { RECEIVED: 'info', ACCEPTED: 'success', REJECTED: 'error', DUPLICATE: 'warning' };
const STATUS_ICONS = { ACCEPTED: <CheckCircleIcon />, REJECTED: <CancelIcon />, DUPLICATE: <ContentCopyIcon /> };
const STATUS_LABELS = { RECEIVED: 'Received', ACCEPTED: 'Accepted', REJECTED: 'Rejected', DUPLICATE: 'Duplicate' };
const statusLabel = (status) => STATUS_LABELS[status]
  || String(status || '—').toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const StatusChip = ({ status }) => (
  <Chip
    size="small"
    variant="outlined"
    color={STATUS_COLORS[status] || 'default'}
    icon={STATUS_ICONS[status]}
    label={statusLabel(status)}
    sx={{ fontWeight: 700 }}
  />
);

const Labeled = ({ label, children }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>{label}:</Typography>
    <Typography variant="body2">{children}</Typography>
  </Stack>
);

const parseData = (value) => {
  try {
    return typeof value === 'string' ? JSON.parse(value || '{}') : (value || {});
  } catch {
    return {};
  }
};

const errorMessage = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

// Mirrors the setup that previously ran at submission time; customer creation is skipped once linked.
export async function runAcceptSetup(referral, setting, compId) {
  const data = parseData(referral.data);
  const coordinator = setting?.referral_coordinator_emp_id;
  if (!coordinator || data.receiving_customer_id) return;

  const name = String(referral.customer_name || data.clientName || data.patientName || '').trim();
  const clientAddress = referralProspectAddress(data);
  const address = clientAddress.address_line_1 ? [clientAddress] : [];
  let dob = referralDateOfBirth(data);

  if (setting.referral_flow_id) {
    if (!dob && data._referral_form_id) {
      try {
        const formResponse = await axios.get(`${APIURL}/task_form/form`, { params: { form_id: data._referral_form_id } });
        const formRow = Array.isArray(formResponse.data) ? formResponse.data[0] : formResponse.data;
        dob = referralDateOfBirth(data, formRow?.json_schema);
      } catch (_) { /* the task endpoint can still resolve custom fields server-side */ }
    }
    if (!name || !address.length) throw new Error('A client name and street address are required to start the referral workflow.');
    const customer = await axios.post(`${APIURL}/customers`, {
      name, address, dob: dob || undefined, company_id: compId, status: 'Active', customer_type: 'Individual', create_project: true, empId: coordinator,
    });
    const customerId = customer.data?.id || customer.data?.cus_id;
    if (!customerId) throw new Error(customer.data?.message || 'The customer could not be created.');
    await axios.put(`${APIURL}/referral/${referral.referral_id}/customer`, { customer_id: customerId, company_id: compId });
    const workflow = await axios.post(`${APIURL}/workflows/instance`, {
      wf_instanceWF_ID: setting.referral_flow_id,
      wf_instanceStatus: 0,
      wf_instanceEmployee: coordinator,
      wf_instanceStartDate: moment().format('MM-DD-YYYY'),
      wf_instanceEndDate: moment().add(30, 'days').format('MM-DD-YYYY'),
      wf_instanceCreatedBy: coordinator,
      wf_instanceCustomer: customerId,
      company_id: compId,
      referral_id: referral.referral_id,
    });
    if (!workflow.data?.id) throw new Error('The customer was created, but the workflow could not be started.');
    return;
  }

  const task = await axios.post(`${APIURL}/referral/${referral.referral_id}/task`, {
    employee_id: coordinator,
    company_id: compId,
    customer_name: name || undefined,
    address,
    dob: dob || undefined,
    initiating_company_name: referral.initiator_name || undefined,
  });
  if (task.data?.code !== 200) throw new Error(task.data?.message || 'The coordinator task could not be created.');
}

const schemaCache = new Map();
const loadTemplate = (formId) => {
  if (!schemaCache.has(formId)) {
    schemaCache.set(formId, axios.get(`${APIURL}/task_form/form`, { params: { form_id: formId } })
      .then((resp) => {
        const row = Array.isArray(resp.data) ? resp.data[0] : resp.data;
        const raw = row?.json_schema;
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (!Array.isArray(parsed?.components)) throw new Error('The referral form template could not be found.');
        // Read-only wizards only render their first page; show every page at once.
        return { title: row.form_name || parsed.title || 'Referral Form', schema: { ...parsed, display: 'form' } };
      })
      .catch((err) => {
        schemaCache.delete(formId);
        throw err;
      }));
  }
  return schemaCache.get(formId);
};

const stop = (e) => e.stopPropagation();

// Older referrals predate the saved template ID, so they fall back to the company's current incoming form.
export function ReferralFormsButton({ referrals, fallbackFormId, clientName }) {
  const [anchor, setAnchor] = useState(null);
  const [forms, setForms] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewing, setViewing] = useState(null);
  const referralsKey = `${referrals.map((r) => `${r.referral_id}:${r.data}`).join('|')}#${fallbackFormId || ''}`;

  useEffect(() => { setForms(null); }, [referralsKey]);

  const openMenu = async (e) => {
    e.stopPropagation();
    setAnchor(e.currentTarget);
    if (forms) return;
    setLoading(true);
    const list = await Promise.all([...referrals]
      .sort((a, b) => String(b.referral_date || '').localeCompare(String(a.referral_date || '')))
      .map(async (referral) => {
        const data = parseData(referral.data);
        const formId = data._referral_form_id || fallbackFormId;
        const template = formId ? await loadTemplate(formId).catch(() => null) : null;
        const answers = referralAnswers(data);
        return {
          id: referral.referral_id,
          title: template?.title || 'Referral Form',
          subtitle: [referral.initiator_name, referral.referral_date && moment(String(referral.referral_date).slice(0, 10)).format('MM/DD/YYYY')]
            .filter(Boolean).join(' · '),
          schema: template?.schema || null,
          templateUnavailable: !!formId && !template,
          answers,
          entries: Object.entries(answers).filter(filledAnswer),
        };
      }));
    setForms(list);
    setLoading(false);
  };

  return (
    <>
      <Tooltip title="View Referral Forms">
        <IconButton aria-label="View Referral Forms" color="primary" size="small" onClick={openMenu}>
          <DescriptionIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} onClick={stop}>
        {loading && (
          <MenuItem disabled>
            <CircularProgress size={16} sx={{ mr: 1 }} /> Loading Forms...
          </MenuItem>
        )}
        {!loading && forms?.map((form) => (
          <MenuItem key={form.id} onClick={() => { setViewing(form); setAnchor(null); }}>
            <ListItemIcon><DescriptionIcon fontSize="small" color="primary" /></ListItemIcon>
            <ListItemText primary={form.title} secondary={form.subtitle || null} />
          </MenuItem>
        ))}
        {!loading && forms && !forms.length && <MenuItem disabled>No Forms Submitted</MenuItem>}
      </Menu>
      <Dialog open={!!viewing} onClose={() => setViewing(null)} maxWidth="md" fullWidth scroll="body" disableRestoreFocus onClick={stop}>
        <DialogTitle sx={{ padding: '16px 24px', borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 'bold' }}>
            {viewing?.title}
            {clientName && <><span style={{ fontWeight: 'normal' }}> for </span>{clientName}</>}
          </Typography>
          {viewing?.subtitle && <Typography variant="body2" color="text.secondary">{viewing.subtitle}</Typography>}
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          {viewing?.templateUnavailable && <Alert severity="info" sx={{ mb: 2 }}>The original form template is unavailable. Saved answers are shown below.</Alert>}
          {viewing?.schema
            ? <Form form={viewing.schema} submission={{ data: viewing.answers }} options={{ readOnly: true, noAlerts: true, hide: { submit: true } }} />
            : viewing?.entries?.length
              ? <ReferralAnswers entries={viewing.entries} />
              : <Typography color="text.secondary">No form answers were submitted.</Typography>}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #e0e0e0', px: 3, py: 1.5 }}>
          <Button variant="outlined" onClick={() => setViewing(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default function IncomingReferrals({ comp_id, user }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [setting, setSetting] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState(null); // null | 'DUPLICATE'
  const [duplicateCustomer, setDuplicateCustomer] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState(null);

  const canDecide = user?.type === 'Admin' || !!user?.permissions?.AdminLevelReferral
    || (setting && Number(setting.referral_coordinator_emp_id) === Number(user?.emp_id));

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get(`${APIURL}/referral`, { params: { comp_id } });
      const incoming = (Array.isArray(resp.data) ? resp.data : []).filter((r) => r.company_role === 'receiving'
        || (!r.company_role && Number(r.receiver_com_id) === Number(comp_id)));
      const awaitingDecision = incoming.filter((r) => String(r.referral_status || '').toUpperCase() === 'RECEIVED');
      setRows(awaitingDecision.map((r) => ({
        id: r.referral_id,
        client: r.customer_name || parseData(r.data).clientName || '—',
        referredBy: r.initiator_name || r.initiator_external_com_name || '—',
        subject: r.referral_subject || 'Referral',
        receivedAt: r.referral_date ? new Date(String(r.referral_date).slice(0, 10) + 'T00:00:00') : null,
        status: String(r.referral_status || '').toUpperCase(),
        data: parseData(r.data),
        raw: r,
      })));
    } catch (err) {
      setError(errorMessage(err, 'Unable to load incoming referrals.'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [comp_id]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  useEffect(() => {
    axios.get(`${APIURL}/referralSettings/${comp_id}/active`)
      .then((resp) => setSetting(resp.data || null))
      .catch(() => setSetting(null));
    axios.get(`${APIURL}/customers/getCustomersList`, { params: { company_id: comp_id } })
      .then((resp) => setCustomers(resp.data?.customers || []))
      .catch(() => setCustomers([]));
  }, [comp_id]);

  const awaitingCount = rows.length;

  const relatedReferrals = useCallback((referral, customerId = parseData(referral.data).receiving_customer_id) => {
    if (!customerId) return [referral];
    const linked = rows.map((r) => r.raw).filter((r) => r.referral_id !== referral.referral_id
      && Number(parseData(r.data).receiving_customer_id) === Number(customerId));
    return [referral, ...linked];
  }, [rows]);

  const selectedData = useMemo(() => parseData(selected?.data), [selected]);
  const awaitingDecision = String(selected?.referral_status || '').toUpperCase() === 'RECEIVED';
  const needsSetupRetry = String(selected?.referral_status || '').toUpperCase() === 'ACCEPTED'
    && selectedData._decided_at && !selectedData.receiving_customer_id && setting?.referral_coordinator_emp_id;

  const openReferral = (referral) => {
    setSelected(referral);
    setMode(null);
    setNote('');
    setActionError(null);
    const suggested = parseData(referral.data)._suggested_customer_id;
    setDuplicateCustomer(customers.find((c) => Number(c.cus_id) === Number(suggested)) || null);
  };

  const closeReferral = () => {
    if (saving) return;
    setSelected(null);
    fetchRows();
  };

  const decide = async (decision) => {
    setSaving(true);
    setActionError(null);
    try {
      const resp = await axios.put(`${APIURL}/referral/${selected.referral_id}/decision`, {
        decision,
        company_id: comp_id,
        emp_id: user?.emp_id,
        customer_id: decision === 'DUPLICATE' ? duplicateCustomer?.cus_id : undefined,
        note: note.trim() || undefined,
      });
      const updated = { ...selected, referral_status: resp.data.referral_status, data: JSON.stringify(resp.data.data || {}) };
      setSelected(updated);
      setMode(null);
      if (decision === 'ACCEPTED') await runAcceptSetup(updated, setting, comp_id);
      setSelected(null);
      fetchRows();
    } catch (err) {
      setActionError(errorMessage(err, 'Unable to save the decision.'));
    } finally {
      setSaving(false);
    }
  };

  const retrySetup = async () => {
    setSaving(true);
    setActionError(null);
    try {
      await runAcceptSetup(selected, setting, comp_id);
      setSelected(null);
      fetchRows();
    } catch (err) {
      setActionError(errorMessage(err, 'Setup failed. Please retry.'));
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(() => [
    { field: 'client', headerName: 'Client', flex: 1, minWidth: 140, renderCell: (p) => <strong>{p.value}</strong> },
    { field: 'referredBy', headerName: 'Referred By', flex: 1, minWidth: 160 },
    { field: 'subject', headerName: 'Subject', flex: 1.5, minWidth: 160 },
    {
      field: 'status', headerName: 'Status', width: 190,
      renderCell: (p) => <StatusChip status={p.value} />,
    },
    {
      field: 'receivedAt', headerName: 'Received', width: 130, type: 'date',
      valueFormatter: (value) => (value ? moment(value).format('MMM D, YYYY') : '—'),
    },
    {
      field: 'forms', headerName: 'Forms', width: 80, sortable: false, filterable: false,
      renderCell: (p) => (
        <ReferralFormsButton referrals={relatedReferrals(p.row.raw)} fallbackFormId={setting?.referral_form_incoming_id || setting?.referral_form_id} clientName={p.row.client} />
      ),
    },
  ], [setting, relatedReferrals]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', p: 2, height: 'calc(100vh - 145px)', overflow: 'hidden' }}>
      <Stack direction="row" spacing={1.25} sx={{ mb: 1.5 }}>
        <StatusCard label={STATUS_META.RECEIVED.label} count={awaitingCount} color={STATUS_META.RECEIVED.color} active />
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <DataGridPro
          rows={rows}
          columns={columns}
          loading={loading}
          rowHeight={36}
          getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 1 ? 'report-row-alternate' : '')}
          disableRowSelectionOnClick
          onRowClick={(params) => openReferral(params.row.raw)}
          initialState={{
            sorting: { sortModel: [{ field: 'receivedAt', sort: 'desc' }] },
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          pagination
          pageSizeOptions={[10, 25, 50, 100]}
          sx={{
            height: '100%',
            backgroundColor: '#F9F9F9',
            '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell, & .MuiDataGrid-filler--pinnedLeft': {
              backgroundColor: '#F9F9F9',
            },
            '& .MuiTablePagination-root': { marginRight: '48px' },
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            '& .report-row-alternate .MuiDataGrid-cell, & .report-row-alternate .MuiDataGrid-filler--pinnedLeft': {
              backgroundColor: '#EAF4FC',
            },
            '& .report-row-alternate:hover .MuiDataGrid-cell, & .report-row-alternate:hover .MuiDataGrid-filler--pinnedLeft': {
              backgroundColor: '#DCEFFC',
            },
          }}
        />
      </Box>

      <Dialog open={!!selected} onClose={closeReferral} maxWidth="md" fullWidth scroll="paper" disableRestoreFocus>
        {selected && (
          <>
            <DialogTitle sx={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '16px 24px', borderBottom: '1px solid #e0e0e0',
            }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {selected.referral_subject || 'Referral'}
              </Typography>
              <StatusChip status={String(selected.referral_status || '').toUpperCase()} />
            </DialogTitle>
            <DialogContent sx={{ pt: '16px !important' }}>
              <Stack spacing={1} sx={{ mb: 2 }}>
                <Labeled label="Client">{selected.customer_name || selectedData.clientName || '—'}</Labeled>
                <Labeled label="Referred By">{selected.initiator_name || '—'}</Labeled>
                <Labeled label="Received">
                  {selected.referral_date ? moment(String(selected.referral_date).slice(0, 10)).format('MM/DD/YYYY') : '—'}
                </Labeled>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>Forms:</Typography>
                  <ReferralFormsButton
                    referrals={relatedReferrals(selected, mode === 'DUPLICATE' && duplicateCustomer
                      ? duplicateCustomer.cus_id : selectedData.receiving_customer_id)}
                    fallbackFormId={setting?.referral_form_incoming_id || setting?.referral_form_id}
                    clientName={selected.customer_name || selectedData.clientName}
                  />
                </Stack>
              </Stack>
              {(awaitingDecision || needsSetupRetry) && <Divider sx={{ my: 2 }} />}
              {awaitingDecision && canDecide && (
                <Stack spacing={2}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {mode === 'DUPLICATE' ? 'Mark as Duplicate' : 'Decision'}
                  </Typography>
                  {mode === 'DUPLICATE' && (
                    <Autocomplete
                      options={customers}
                      value={duplicateCustomer}
                      onChange={(_, v) => setDuplicateCustomer(v)}
                      getOptionLabel={(o) => `${o.cus_name} (#${o.cus_id})`}
                      isOptionEqualToValue={(o, v) => o.cus_id === v.cus_id}
                      renderInput={(params) => <TextField {...params} size="small" label="Existing Customer" />}
                    />
                  )}
                  <TextField size="small" label="Note (Optional)" multiline rows={2} fullWidth value={note} onChange={(e) => setNote(e.target.value)} />
                </Stack>
              )}
              {awaitingDecision && !canDecide && (
                <Alert severity="info">Only an admin or the referral coordinator can decide this referral.</Alert>
              )}
              {needsSetupRetry && <Alert severity="warning">Accepted, but setup did not finish.</Alert>}
              {actionError && <Alert severity="error" sx={{ mt: 2 }}>{actionError}</Alert>}
            </DialogContent>
            <DialogActions sx={{ borderTop: '1px solid #e0e0e0', px: 3, py: 1.5 }}>
              {saving && <CircularProgress size={20} sx={{ mr: 'auto' }} />}
              {awaitingDecision && canDecide && mode !== 'DUPLICATE' && (
                <>
                  <Button variant="outlined" color="error" disabled={saving} onClick={() => decide('REJECTED')}>Reject</Button>
                  <Button variant="outlined" color="primary" disabled={saving} onClick={() => setMode('DUPLICATE')}>Duplicate</Button>
                  <Button variant="contained" color="primary" disabled={saving} onClick={() => decide('ACCEPTED')}>Accept</Button>
                </>
              )}
              {awaitingDecision && canDecide && mode === 'DUPLICATE' && (
                <>
                  <Button variant="outlined" disabled={saving} onClick={() => setMode(null)}>Back</Button>
                  <Button variant="contained" color="primary" disabled={saving || !duplicateCustomer} onClick={() => decide('DUPLICATE')}>
                    Mark Duplicate
                  </Button>
                </>
              )}
              {needsSetupRetry && canDecide && (
                <Button variant="contained" color="primary" disabled={saving} onClick={retrySetup}>Retry Setup</Button>
              )}
              <Button variant="outlined" disabled={saving} onClick={closeReferral}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
